import { Check } from '@untitledui/icons';
import { isValidElement, type ReactNode, useContext } from 'react';
import type {
  ListBoxItemProps as AriaListBoxItemProps,
  ListBoxItemRenderProps,
} from 'react-aria-components';
import {
  ListBoxItem as AriaListBoxItem,
  Text as AriaText,
} from 'react-aria-components';

import { Avatar } from '@/lib/base/avatar/avatar';
import { cx } from '@/utils/cx';
import { isReactComponent } from '@/utils/is-react-component';

import {
  SelectContext,
  type SelectItemType,
  type SelectSize,
} from './select-shared';

// Option-row padding per trigger size. Rows track the trigger's scale so the
// dropdown reads as part of the same control; `lg`/`xl` share `md`'s roomier
// row rather than growing further — an option list with 60px rows scrolls
// badly and the extra height buys nothing.
const sizes: Record<SelectSize, string> = {
  xs: 'p-1.5 pr-2',
  sm: 'p-2 pr-2.5',
  md: 'p-2.5 pl-2',
  lg: 'p-2.5 pl-2',
  xl: 'p-2.5 pl-2',
};

interface SelectItemProps
  extends Omit<AriaListBoxItemProps<SelectItemType>, 'id'>, SelectItemType {}

export const SelectItem = ({
  label,
  id,
  value,
  avatarUrl,
  supportingText,
  description,
  isDisabled,
  icon: Icon,
  className,
  children,
  textValue: textValueProp,
  ...props
}: SelectItemProps) => {
  const { size } = useContext(SelectContext);

  const labelOrChildren =
    label || (typeof children === 'string' ? children : '');
  // An explicit textValue overrides the label-derived one so callers can make
  // an item findable by text that isn't shown (e.g. its section/group name).
  const textValue =
    textValueProp ??
    (supportingText ? labelOrChildren + ' ' + supportingText : labelOrChildren);

  // Prevent disabled items from closing the popover by stopping event propagation
  const preventCloseHandlers = isDisabled
    ? {
        onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
        onPointerUp: (e: React.PointerEvent) => e.stopPropagation(),
        onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
        onMouseUp: (e: React.MouseEvent) => e.stopPropagation(),
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      }
    : {};

  return (
    <AriaListBoxItem
      id={id}
      value={
        value ?? {
          id,
          label: labelOrChildren,
          avatarUrl,
          supportingText,
          description,
          isDisabled,
          icon: Icon,
        }
      }
      textValue={textValue}
      isDisabled={isDisabled}
      {...props}
      {...preventCloseHandlers}
      className={(state: ListBoxItemRenderProps) =>
        cx(
          'w-full px-1.5 py-px outline-hidden',
          typeof className === 'function'
            ? className(state as never)
            : className
        )
      }
    >
      {(state: ListBoxItemRenderProps) => {
        let leadingIcon: ReactNode = null;
        if (avatarUrl) {
          leadingIcon = (
            <Avatar aria-hidden='true' size='xs' src={avatarUrl} alt={label} />
          );
        } else if (isReactComponent(Icon)) {
          leadingIcon = <Icon data-icon aria-hidden='true' />;
        } else if (isValidElement(Icon)) {
          leadingIcon = Icon;
        }

        return (
          <div
            className={cx(
              'flex cursor-pointer items-center gap-2 rounded-md outline-hidden select-none',
              state.isSelected && 'bg-active',
              state.isDisabled && 'cursor-not-allowed',
              state.isFocused && 'bg-primary_hover',
              state.isFocusVisible && 'ring-2 ring-focus-ring ring-inset',

              // Icon styles
              '*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary',
              state.isDisabled && '*:data-icon:text-fg-disabled',

              sizes[size]
            )}
          >
            {leadingIcon}

            <div className='flex w-full min-w-0 flex-1 flex-col'>
              <div className='flex flex-wrap gap-x-2'>
                <AriaText
                  slot='label'
                  className={cx(
                    'truncate text-md font-medium whitespace-nowrap text-primary',
                    state.isDisabled && 'text-disabled'
                  )}
                >
                  {label ||
                    (typeof children === 'function'
                      ? children(state as never)
                      : children)}
                </AriaText>

                {supportingText && (
                  <AriaText
                    slot='description'
                    className={cx(
                      'text-md whitespace-nowrap text-tertiary',
                      state.isDisabled && 'text-disabled'
                    )}
                  >
                    {supportingText}
                  </AriaText>
                )}
              </div>

              {description && (
                <p
                  className={cx(
                    'text-xs text-fg-tertiary',
                    state.isDisabled && 'text-disabled'
                  )}
                >
                  {description}
                </p>
              )}
            </div>

            {state.isSelected && (
              <Check
                aria-hidden='true'
                className={cx(
                  'ml-auto',
                  'text-[var(--color-fg-brand-primary)]',
                  size === 'sm' ? 'size-4 stroke-[2.5px]' : 'size-5',
                  state.isDisabled && 'text-fg-disabled'
                )}
              />
            )}
          </div>
        );
      }}
    </AriaListBoxItem>
  );
};
